import { useEffect } from 'react';
import siteMetadata from '@/generated/site-metadata.json';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://glassexpertsingapore.com';
const DEFAULT_OG_IMAGE =
  'https://glassexpertsingapore.com/images/ext/og-default-1-800x420-1200.53e5f1ad.jpg';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  type?: string;
  ogImage?: string;
  twitterImage?: string;
  canonical?: string;
  noindex?: boolean;
  lastModified?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  type = 'website',
  ogImage,
  twitterImage,
  canonical,
  noindex = false,
  lastModified,
  structuredData,
}: SEOHeadProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const approved = (siteMetadata as Record<string, {title: string; description?: string; canonical?: string}>)[pathname.replace(/\/$/, '') || '/'];
  title = approved?.title || title;
  // Downloads now contains the published catalogues; use its current source description.
  description = pathname.replace(/\/$/, '') === '/downloads' ? description : (approved?.description || description);
  const canonicalUrl = approved?.canonical || canonical || `${BASE_URL}${pathname}`;
  const image = ogImage || DEFAULT_OG_IMAGE;
  const twImage = twitterImage || image;

  const renderSchema = () => {
    if (!structuredData) return null;
    const schemas = Array.isArray(structuredData) ? structuredData : [structuredData];
    return schemas.map((data, i) => (
      <script
        key={i}
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      />
    ));
  };

  // Update the one document head instead of hoisting a second title/canonical
  // alongside the static SEO head. The approved route map also applies on SPA
  // navigation, so rebuilding cannot silently replace the published SEO choices.
  useEffect(() => {
    function update(selector: string, tag: string, attributes: Record<string, string>) {
      const matches = [...document.head.querySelectorAll(selector)];
      const node = matches.shift() || document.head.appendChild(document.createElement(tag));
      Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
      matches.forEach(extra => extra.remove());
    }
    document.title = title;
    document.head.querySelectorAll('title').forEach((node, index) => { if(index)node.remove(); });
    update('meta[name="description"]', 'meta', {name:'description', content:description});
    update('link[rel="canonical"]', 'link', {rel:'canonical', href:canonicalUrl});
    update('meta[name="robots"]', 'meta', {name:'robots',content:noindex?'noindex, follow':'index, follow, max-image-preview:large'});
    for(const [property, content] of Object.entries({'og:title':title,'og:description':description,'og:type':type,'og:url':canonicalUrl,'og:image':image}))
      update(`meta[property="${property}"]`, 'meta', {property,content});
    for(const [name, content] of Object.entries({'twitter:card':'summary_large_image','twitter:title':title,'twitter:description':description,'twitter:image':twImage}))
      update(`meta[name="${name}"]`, 'meta', {name,content});
    if(keywords)update('meta[name="keywords"]','meta',{name:'keywords',content:keywords});
    if(lastModified)update('meta[name="last-modified"]','meta',{name:'last-modified',content:lastModified});
  }, [title, description, canonicalUrl, noindex, type, image, twImage, keywords, lastModified]);
  return <>{renderSchema()}</>;
}
